import {afterNextRender, ChangeDetectionStrategy, Component, DestroyRef, ElementRef, EventEmitter, Injectable, Injector, input, Output, signal, Signal, viewChild} from "@angular/core";
import {AgendaelementId, AgendaelementModell, AvstemningsstatusModell, GruppeId, MøteId, MøteRessursClient, SaksreferatSpesifikasjon} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {distinctUntilChanged, filter, switchMap} from "rxjs";
import {takeUntilDestroyed, toObservable, toSignal} from "@angular/core/rxjs-interop";
import {MøteprotokollRedigerSaksreferatKomponent} from "web-ng2/moduler/møteprotokoll/komponenter/møteprotokoll-rediger-saksreferat/møteprotokoll-rediger-saksreferat";
import {førsteVerdiLevertFraServer} from "felles-ng2/tjenester/datalager/DataOgMeta";
import {i18n} from "felles-ng2/tjenester/i18n/Oversettelser";
import {AgendaelementModellDLM, SakDatatjeneste} from "felles-ng2/tjenester/datalager/SakDatatjeneste";
import {tomAgendaelementModell, tomAvstemningsstatusModell} from "felles-ng2/modell/tommeModeller";
import {asynkArbeidPågårSignal} from "felles-ng2/tjenester/AsynkArbeidPågårSignal";
import {NavigasjonDeaktivering} from "web-ng2/tjenester/NavigasjonDeaktivering";
import {InlineSkjemaKomponent} from "web-ng2/delt/komponenter/inline-skjema/inline-skjema";
import {ReactiveFormsModule, StatusChangeEvent} from "@angular/forms";
import {InlineSkjemaHandlingerKomponent} from "web-ng2/delt/komponenter/inline-skjema/inline-skjema-handlinger/inline-skjema-handlinger";
import {MatButton} from "@angular/material/button";
import {MatMenu, MatMenuItem, MatMenuTrigger} from "@angular/material/menu";
import {NavigasjonforhindretFlashDirektiv} from "web-ng2/delt/direktiver/navigasjonforhindret-flash/navigasjonforhindret-flash";
import {SubmitButtonKomponent} from "web-ng2/delt/komponenter/skjema/submit-button/submit-button";
import {TranslateModule} from "@ngx-translate/core";
import {map} from "rxjs/operators";
import {InlineSkjemaTittelKomponent} from "web-ng2/delt/komponenter/inline-skjema/inline-skjema-tittel/inline-skjema-tittel";
import {antallKontrollerMedFeil} from "felles-ng2/infrastruktur/angular/reactiveForm";
import {AnimationBuilder} from "@angular/animations";
import {fokuserPåFørsteFeltMedFeil} from "web-ng2/delt/material/materialFormFieldHjelpere";

const debug = initDebugger('SakRedigerReferatKomponent');

/**
 * Skjema for å redigere og lagre saksreferat
 */
@Component({
    selector: 'sak-rediger-referat',
    templateUrl: 'sak-rediger-referat.html',
    styleUrl: 'sak-rediger-referat.scss',
    imports: [
        MøteprotokollRedigerSaksreferatKomponent,
        InlineSkjemaKomponent,
        ReactiveFormsModule,
        InlineSkjemaHandlingerKomponent,
        MatButton,
        MatMenu,
        MatMenuItem,
        NavigasjonforhindretFlashDirektiv,
        SubmitButtonKomponent,
        TranslateModule,
        MatMenuTrigger,
        InlineSkjemaTittelKomponent
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SakRedigerReferatKomponent {
    static ajsSelector = 'sakRedigerReferat';

    private redigerReferatKomponent = viewChild.required(MøteprotokollRedigerSaksreferatKomponent);

    @Output() vedAvsluttet = new EventEmitter<boolean>();

    sakId = input.required<AgendaelementId>();
    moeteId = input.required<MøteId>();
    gruppeId = input.required<GruppeId>();

    skjema = MøteprotokollRedigerSaksreferatKomponent.populerSkjema(MøteprotokollRedigerSaksreferatKomponent.opprettSkjema(), tomAgendaelementModell());
    avstemningsstatus: Signal<AvstemningsstatusModell>;
    antallBeslutningsforslag = signal(0);
    antallFeil = signal(0);
    lagrer = asynkArbeidPågårSignal();

    constructor(private avhengigheter: SakRedigerReferatKomponentAvhengigheter,
                private navigasjonDeaktivering: NavigasjonDeaktivering,
                private el: ElementRef<HTMLElement>,
                private animationBuilder: AnimationBuilder,
                private injector: Injector,
                private destroyRef: DestroyRef) {
        this.navigasjonDeaktivering.deaktiverNavigasjon();
        this.oppdaterAntallBeslutningsforslagVedEndring();

        let idSomObserverbar = toObservable(this.sakId);
        idSomObserverbar.pipe(
          switchMap(id => this.avhengigheter.lastSak(id)),
          takeUntilDestroyed()
        )
          .subscribe(sak => {
              MøteprotokollRedigerSaksreferatKomponent.populerSkjema(this.skjema, sak);
              //oppdater antall feil ved skjemaendringer - bør ikke gjøres før skjema har blitt fullt populert, derfor her...
              this.oppdaterAntallFeilVedSkjemaendringer();
              //sett markør i referat-feltet, men gi tid til at felt rendres - for sikkerhets skyld
              afterNextRender({read: () => this.redigerReferatKomponent().giReferatFeltFokus()}, {injector: this.injector});
          });

        this.avstemningsstatus = toSignal(toObservable(this.moeteId).pipe(
          switchMap(id => this.avhengigheter.lastAvstemningsstatus(id)),
          takeUntilDestroyed()
        ), {initialValue: tomAvstemningsstatusModell()});
    }


    limInnSaksbeskrivelse() {
        MøteprotokollRedigerSaksreferatKomponent.kopierSaksbeskrivelseTilReferat(this.skjema);
    }

    limInnAlleBeslutningsforslag() {
        this.skjema.controls.beslutninger.controls.eksisterende.controls
          .filter(beslutning => beslutning.value.erForslag)
          .forEach(forslag => forslag.controls.erForslag.setValue(false));
    }

    gaaTilFoersteFeil() {
        fokuserPåFørsteFeltMedFeil(this.el.nativeElement, this.animationBuilder)
    }

    async lagre() {
        await this.lagrer.utfør(async () => this.avhengigheter.lagre(this.somSpesifikasjon()));
        await this.navigasjonDeaktivering.reaktiverNavigasjon();
        this.vedAvsluttet.emit(true);
    }

    async avbryt() {
        await this.navigasjonDeaktivering.reaktiverNavigasjon();
        this.vedAvsluttet.emit(false);
    }

    //private hjelpemetoder

    private oppdaterAntallFeilVedSkjemaendringer() {
        this.skjema.events
          .pipe(
            filter(event => event instanceof StatusChangeEvent),
            map(() => antallKontrollerMedFeil(this.skjema)),
            takeUntilDestroyed(this.destroyRef))
          .subscribe(antall => this.antallFeil.set(antall))
    }

    private oppdaterAntallBeslutningsforslagVedEndring() {
        this.skjema.controls.beslutninger!.controls.eksisterende!.valueChanges
          .pipe(
            map(beslutninger => beslutninger.filter(beslutning => beslutning.erForslag).length),
            distinctUntilChanged(),
            takeUntilDestroyed(this.destroyRef))
          .subscribe(antallForslag => this.antallBeslutningsforslag.set(antallForslag))
    }

    private somSpesifikasjon(): SaksreferatSpesifikasjon {
        //må bruke getRawValue ellers blir ikke slettede beslutninger/oppgaver inkludert (disse har blitt markert som disabled - og blir derfor ikke inkludert i skjema.value)
        return MøteprotokollRedigerSaksreferatKomponent.somSpesifikasjon(this.skjema.getRawValue());
    }
}

@Injectable()
export class SakRedigerReferatKomponentAvhengigheter {

    constructor(private sakDatatjeneste: SakDatatjeneste, private møteRessurs: MøteRessursClient) {
    }

    lastSak(id: AgendaelementId): Promise<AgendaelementModellDLM> {
        return førsteVerdiLevertFraServer(this.sakDatatjeneste.hentMedMeta(id));
    }

    lastAvstemningsstatus(id: MøteId): Promise<AvstemningsstatusModell> {
        return this.møteRessurs.avstemningsstatus(id).somPromiseVisMeldingVedFeil(i18n('Kunne ikke hente avstemningsstatus'));
    }

    async lagre(spesifikasjon: SaksreferatSpesifikasjon): Promise<AgendaelementModellDLM> {
        let oppdatertSak = await this.sakDatatjeneste.oppdaterReferatOgBeslutninger(spesifikasjon);
        return oppdatertSak.data
    }

}
